import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, CheckCircle, Award, Notebook, Users, ArrowRight, Clock } from 'lucide-react';
import { portalApi, usePortalAuth } from '../../contexts/PortalAuthContext';

export default function PortalDashboard() {
  const { user } = usePortalAuth();
  const [modules, setModules] = useState([]);
  const [progress, setProgress] = useState(null);
  const [cohortCount, setCohortCount] = useState(0);
  const [journalCount, setJournalCount] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const [m, p, c, j] = await Promise.all([
          portalApi.get('/modules'),
          portalApi.get('/progress'),
          portalApi.get('/cohort'),
          portalApi.get('/journal'),
        ]);
        setModules(m.data);
        setProgress(p.data);
        setCohortCount(c.data.length);
        setJournalCount(j.data.length);
      } catch (e) { /* gracefully ignore */ }
    })();
  }, []);

  const pct = progress?.completion_percent ?? 0;
  const nextModule = modules.find(m => !m.progress?.module_completed);

  return (
    <div data-testid="portal-dashboard">
      <header className="mb-10">
        <div className="text-amber-400 text-xs uppercase tracking-widest mb-2">Welcome back</div>
        <h1 className="text-3xl sm:text-4xl font-semibold text-white">
          Hello, {(user?.full_name || '').split(' ')[0]} 👋
        </h1>
        <p className="text-slate-400 mt-2">Here&apos;s where you are on the programme.</p>
      </header>

      {/* Progress hero */}
      <section className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-900/50 border border-slate-800 p-6 sm:p-8 mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
          <div>
            <div className="text-sm text-slate-400 mb-1">Programme completion</div>
            <div className="text-5xl font-bold text-white tabular-nums">
              {pct}<span className="text-2xl text-slate-500">%</span>
            </div>
            <div className="text-sm text-slate-400 mt-2">
              {progress?.completed_modules ?? 0} of {progress?.total_modules ?? 0} modules complete
            </div>
          </div>
          <div className="w-full sm:w-auto sm:min-w-[260px]">
            <div className="h-3 bg-slate-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-amber-500 to-orange-600 transition-all duration-700"
                style={{ width: `${pct}%` }}
                data-testid="portal-progress-bar"
              />
            </div>
            {pct >= 100 ? (
              <Link to="/portal/certificate" className="mt-4 flex items-center gap-2 text-amber-400 hover:text-amber-300 text-sm font-medium">
                <Award className="w-4 h-4" /> View your certificate <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            ) : nextModule ? (
              <Link to={`/portal/modules/${nextModule.id}`} className="mt-4 flex items-center gap-2 text-amber-400 hover:text-amber-300 text-sm font-medium" data-testid="portal-resume-link">
                Resume Module {nextModule.number} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            ) : null}
          </div>
        </div>
      </section>

      {/* Stat cards */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-10">
        <StatCard icon={BookOpen} label="Modules" value={progress?.total_modules ?? '–'} color="amber" />
        <StatCard icon={CheckCircle} label="Completed" value={progress?.completed_modules ?? '–'} color="emerald" />
        <StatCard icon={Notebook} label="Journal entries" value={journalCount} color="sky" />
        <StatCard icon={Users} label="Cohort" value={cohortCount} color="violet" />
      </section>

      {/* Module grid */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-white">Your modules</h2>
          <Link to="/portal/modules" className="text-sm text-amber-400 hover:underline">View all →</Link>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {modules.slice(0, 6).map(m => (
            <ModuleCard key={m.id} module={m} />
          ))}
          {modules.length === 0 && (
            <div className="col-span-2 text-slate-500 text-sm">Modules will appear here once published.</div>
          )}
        </div>
      </section>
    </div>
  );
}

const colorMap = {
  amber: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
  emerald: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
  sky: 'text-sky-400 bg-sky-500/10 border-sky-500/20',
  violet: 'text-violet-400 bg-violet-500/10 border-violet-500/20',
};

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className={`rounded-xl border bg-slate-900/60 p-4 ${colorMap[color]}`}>
      <Icon className="w-5 h-5 mb-3" />
      <div className="text-2xl font-bold text-white tabular-nums">{value}</div>
      <div className="text-xs text-slate-400 mt-1">{label}</div>
    </div>
  );
}

function ModuleCard({ module: m }) {
  const tasks = m.progress?.tasks_total || 0;
  const done = m.progress?.tasks_completed || 0;
  const tpct = tasks ? Math.round(100 * done / tasks) : 0;
  return (
    <Link
      to={`/portal/modules/${m.id}`}
      className="group block rounded-xl border border-slate-800 bg-slate-900/60 hover:border-amber-500/40 hover:bg-slate-900 p-5 transition"
      data-testid={`module-card-${m.number}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="text-xs uppercase tracking-wider text-slate-500">Module {String(m.number).padStart(2, '0')}</div>
        {m.progress?.module_completed && (
          <span className="text-xs text-emerald-400 flex items-center gap-1">
            <CheckCircle className="w-3.5 h-3.5" /> Done
          </span>
        )}
      </div>
      <h3 className="text-white font-semibold leading-snug mb-1 group-hover:text-amber-400 transition">{m.title}</h3>
      {m.subtitle && <p className="text-sm text-slate-400 mb-4 line-clamp-2">{m.subtitle}</p>}
      <div className="flex items-center justify-between text-xs text-slate-500">
        <div className="flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5" /> {m.duration_minutes || 45} min
        </div>
        <div>{done}/{tasks} tasks</div>
      </div>
      <div className="mt-3 h-1 bg-slate-800 rounded-full overflow-hidden">
        <div className="h-full bg-amber-500" style={{ width: `${tpct}%` }} />
      </div>
    </Link>
  );
}
